import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Layout } from '@/components/layout';
import { Card, Button, Spinner, ErrorState, Modal, Textarea, Badge } from '@/components/atoms';
import { useAuthStore } from '@/store';
import { useBackButton, useHapticFeedback } from '@/hooks';
import { orders as ordersApi } from '@/api';
import { toast } from '@/components/atoms/Toast';
import type { Order } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function OrderDetailPage(): JSX.Element {
  useBackButton();
  const haptic = useHapticFeedback();
  const { id = '' } = useParams<{ id: string }>();
  const orderId = Number(id);
  const me = useAuthStore((s) => s.user);
  const [order, setOrder] = useState<Order | null>(null);
  const [errored, setErrored] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [sanctionOpen, setSanctionOpen] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(orderId)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await ordersApi.fetchOrder(orderId);
        if (!cancelled) setOrder(data);
      } catch (err) {
        logger.warn('Order fetch failed', err);
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (errored) return <ErrorState onRetry={() => setErrored(false)} />;
  if (!order)
    return (
      <Layout title="Замовлення">
        <Spinner center />
      </Layout>
    );

  const isSpecialist = me?.id === order.specialist_id;
  const isClient = me?.id === order.client_id;

  const accept = async (): Promise<void> => {
    try {
      const updated = await ordersApi.acceptOrder(order.id);
      setOrder(updated);
      haptic.notification('success');
      toast.success('Прийнято');
    } catch (err) {
      logger.error('Accept failed', err);
      toast.error('Не вдалось прийняти');
    }
  };

  const start = async (): Promise<void> => {
    try {
      const updated = await ordersApi.startOrder(order.id);
      setOrder(updated);
    } catch {
      toast.error('Не вдалось почати');
    }
  };

  const complete = async (): Promise<void> => {
    try {
      const updated = await ordersApi.completeOrder(order.id);
      setOrder(updated);
      toast.success('Завершено. Залиш відгук.');
    } catch {
      toast.error('Не вдалось завершити');
    }
  };

  const cancel = async (): Promise<void> => {
    try {
      const resp = await ordersApi.cancelOrder(order.id, {
        reason: cancelReason,
        confirm_outside_range: true,
      });
      setCancelOpen(false);
      setCancelReason('');
      if (resp.rating_impact_warning) toast.info(resp.rating_impact_warning);
      const updated = await ordersApi.fetchOrder(order.id);
      setOrder(updated);
      if (resp.specialist_can_sanction && isSpecialist) setSanctionOpen(true);
    } catch (err) {
      logger.error('Cancel failed', err);
      toast.error('Не вдалось скасувати');
    }
  };

  const sanction = async (kind: 'note' | 'block' | 'none'): Promise<void> => {
    try {
      await ordersApi.sanctionClient(order.id, kind);
      setSanctionOpen(false);
      toast.success(kind === 'none' ? 'Без санкцій' : 'Застосовано');
    } catch {
      toast.error('Не вдалось зберегти');
    }
  };

  return (
    <Layout title={order.title}>
      <Card>
        <div className={styles.kvList}>
          <span className={styles.kvKey}>Статус</span>
          <Badge>{order.status_label || order.status}</Badge>
          <span className={styles.kvKey}>Ціна</span>
          <strong>{Number(order.price_eur).toFixed(2)} €</strong>
          {order.scheduled_at ? (
            <>
              <span className={styles.kvKey}>Час</span>
              <span>{dayjs(order.scheduled_at).format('D MMM YYYY, HH:mm')}</span>
            </>
          ) : null}
          {order.duration_minutes ? (
            <>
              <span className={styles.kvKey}>Тривалість</span>
              <span>{order.duration_minutes} хв</span>
            </>
          ) : null}
          {order.description ? (
            <>
              <span className={styles.kvKey}>Опис</span>
              <span>{order.description}</span>
            </>
          ) : null}
        </div>
      </Card>

      <div className={styles.actions}>
        {isClient && order.status === 'proposed' ? <Button onClick={accept}>Прийняти</Button> : null}
        {isSpecialist && order.status === 'accepted' ? (
          <Button onClick={start}>Розпочати</Button>
        ) : null}
        {isSpecialist && order.status === 'in_progress' ? (
          <Button onClick={complete}>Завершити</Button>
        ) : null}
        {order.status !== 'cancelled' && order.status !== 'completed' ? (
          <Button variant="destructive" onClick={() => setCancelOpen(true)}>
            Скасувати
          </Button>
        ) : null}
      </div>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Скасувати замовлення?"
        actions={
          <>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              Назад
            </Button>
            <Button variant="destructive" onClick={cancel}>
              Так, скасувати
            </Button>
          </>
        }
      >
        <Textarea
          label="Причина (необов'язково)"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          maxLength={500}
          rows={3}
        />
        <p className={styles.note}>
          Якщо скасування поза «ренжем комфорту» — рейтинг може знизитися на 10 пунктів.
        </p>
      </Modal>

      <Modal
        open={sanctionOpen}
        onClose={() => sanction('none')}
        title="Скасовано поза ренжем"
        actions={
          <>
            <Button variant="ghost" onClick={() => sanction('none')}>
              Без санкцій
            </Button>
            <Button variant="secondary" onClick={() => sanction('note')}>
              Помітка
            </Button>
            <Button variant="destructive" onClick={() => sanction('block')}>
              Блокувати
            </Button>
          </>
        }
      >
        <p>Клієнт скасував замовлення поза твоїм ренжем комфорту. Як вчиниш?</p>
      </Modal>
    </Layout>
  );
}
